// ✅ src/pages/Sales/FullPaymentReport.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaUser,
  FaHashtag,
  FaCalendarAlt,
  FaPrint,
} from "react-icons/fa";
import SalesLayoutWrapper from "../../components/SalesLayoutWrapper";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "http://nissi-india.com";

const FullPaymentReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = `bg-${baseColor}-${baseShade}`;

  const [showFilters, setShowFilters] = useState(true);

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // 🔎 Search form
  const [searchData, setSearchData] = useState({
    enquiryNumber: "",
    customerName: "",
    customerId: "",
    assetSubTypeId: "",
    doB: "",
  });

  // ✅ keep last searched params for print
  const [lastSearchParams, setLastSearchParams] = useState(null);

  // Table data from API
  const [customerData, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ asset subtype list from API
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const [assetLoading, setAssetLoading] = useState(false);

  // ---------------------------
  // Load Asset Sub Types (API)
  // ---------------------------
  useEffect(() => {
    const loadAssetSubTypes = async () => {
      try {
        setAssetLoading(true);

        const res = await axios.get(
          `${BASE_URL}/api/fullPaymentListing/assetSubTypes`,
          {
            headers: commonHeaders,
          }
        );

        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setAssetSubTypes(list);
      } catch (err) {
        console.error("asset subtype load error", err);
        toast.error("Failed to load Asset Sub Types");
      } finally {
        setAssetLoading(false);
      }
    };

    loadAssetSubTypes();
  }, [commonHeaders]);

  const handleInputChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  // ---------------------------
  // Search API Call
  // ---------------------------
  const handleSearch = async () => {
    const hasSearchCriteria = Object.values(searchData).some(
      (v) => String(v || "").trim() !== ""
    );

    if (!hasSearchCriteria) {
      toast.warning("Please enter at least one search criteria!");
      return;
    }

    try {
      setLoading(true);

      const params = {
        enquiryNumber: searchData.enquiryNumber || "",
        customerName: searchData.customerName || "",
        customerId: searchData.customerId || "",
        assetSubTypeId: searchData.assetSubTypeId || "",
        dob: searchData.doB || "",
      };

      const res = await axios.get(`${BASE_URL}/api/fullPaymentListing/search`, {
        params,
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        setCustomerData([]);
        toast.error(res.data?.message || "Search failed");
        return;
      }

      setCustomerData(Array.isArray(res.data.data) ? res.data.data : []);
      setLastSearchParams(params);

      toast.success("Search completed!");
    } catch (err) {
      console.error("full payment search error", err);
      setCustomerData([]);
      toast.error(err?.response?.data?.message || "Server error while searching");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // Print (pass same params)
  // ---------------------------
  const handleAllPrint = () => {
    const params = lastSearchParams || {
      enquiryNumber: searchData.enquiryNumber || "",
      customerName: searchData.customerName || "",
      customerId: searchData.customerId || "",
      assetSubTypeId: searchData.assetSubTypeId || "",
      dob: searchData.doB || "",
    };

    const qs = new URLSearchParams({
      ...params,
      finYearId,
      finYear,
      branchId,
      branch,
      userId,
    }).toString();

    window.open(`/Sales/FullPaymentReport_AllPrint?${qs}`, "_blank");
  };

  const formatAmount = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(Number(amount || 0));

  const getPaymentStatusColor = (status) => {
    switch (String(status || "").trim().toLowerCase()) {
      case "completed":
      case "paid":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const totalAmount = useMemo(
    () => customerData.reduce((sum, c) => sum + Number(c.amount || 0), 0),
    [customerData]
  );

  const completedCount = useMemo(
    () =>
      customerData.filter(
        (c) => String(c.paymentStatus || "").trim().toLowerCase() === "completed"
      ).length,
    [customerData]
  );

  return (
    <SalesLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">FULL PAYMENT CUSTOMER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Full Payment Report
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
                Search Criteria
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaHashtag className="inline mr-2" />
                    Enquiry Number
                  </label>
                  <input
                    type="text"
                    value={searchData.enquiryNumber}
                    onChange={(e) => handleInputChange("enquiryNumber", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaUser className="inline mr-2" />
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={searchData.customerName}
                    onChange={(e) => handleInputChange("customerName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaHashtag className="inline mr-2" />
                    Customer ID
                  </label>
                  <input
                    type="text"
                    value={searchData.customerId}
                    onChange={(e) => handleInputChange("customerId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Asset Sub Type
                  </label>
                  <select
                    value={searchData.assetSubTypeId}
                    onChange={(e) => handleInputChange("assetSubTypeId", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">{assetLoading ? "Loading..." : "Select"}</option>

                    {assetSubTypes.map((t) => {
                      const value = t.id ?? t.fld_assetsubtypeid ?? "";
                      const label =
                        t.name ?? t.fld_assetsubtypename ?? t.assetSubTypeName ?? "";

                      return (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    <FaCalendarAlt className="inline mr-2" />
                    DoB
                  </label>
                  <input
                    type="date"
                    value={searchData.doB}
                    onChange={(e) => handleInputChange("doB", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div className="flex justify-center gap-6">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  onClick={handleAllPrint}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold text-lg shadow-lg ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPrint />
                  Print
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
              <thead className={`${tableHeaderClass} text-white`}>
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Sl No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Customer ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Bill No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Customer Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Plot Number</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Asset Code</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Asset Type / Model</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold">Payment Status</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {customerData.map((c, idx) => (
                  <tr
                    key={idx}
                    className={`${
                      idx % 2 === 0
                        ? isDark
                          ? "bg-gray-800"
                          : "bg-white"
                        : isDark
                        ? "bg-gray-750"
                        : "bg-gray-50"
                    } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"} transition-colors`}
                  >
                    <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {c.slNo}
                    </td>
                    <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                      {c.customerId}
                    </td>
                    <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                      {c.billNo}
                    </td>
                    <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                      {c.customerName}
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {c.plotNumber}
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {c.assetCode}
                    </td>
                    <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                      {c.assetTypeModel || c.assetSubTypeName}
                    </td>
                    <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                      {formatAmount(c.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(
                          c.paymentStatus
                        )}`}
                      >
                        {c.paymentStatus}
                      </span>
                    </td>
                  </tr>
                ))}

                {!loading && customerData.length === 0 && (
                  <tr>
                    <td colSpan={9} className={`px-6 py-8 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      No data
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div
            className={`mt-6 p-4 rounded-lg border ${
              isDark ? "bg-gray-800/30 border-gray-600" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex justify-between items-center">
              <div>
                <h4 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Search Results
                </h4>
                <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Showing {customerData.length} full payment customers
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-center">
                <div className={`p-3 rounded-lg ${isDark ? "bg-green-900/30" : "bg-green-50"}`}>
                  <div className="text-lg font-bold text-green-600">{formatAmount(totalAmount)}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>Total Amount</div>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? "bg-blue-900/30" : "bg-blue-50"}`}>
                  <div className="text-lg font-bold text-blue-600">{completedCount}</div>
                  <div className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>Completed</div>
                </div>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </SalesLayoutWrapper>
  );
};

export default FullPaymentReport;